import { useEffect, useState } from "react";
import { useWallet } from "@/context/WalletContext";
import { getKeys, createKey, revokeKey, getUsage, getEndpoints, fmtNum } from "@/lib/api";
import { PageHeader, StatCard } from "@/dashboard/ui";
import { Input } from "@/components/ui/input";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Eye, EyeOff, Check, KeyRound, Terminal, Loader2 } from "lucide-react";

function UsageTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="border border-welock-border bg-welock-surface px-3 py-2 font-mono text-xs">
      <div className="text-welock-textMuted">{label}</div>
      <div className="text-welock-accent">{fmtNum(payload[0].value)} req</div>
    </div>
  );
}

export default function Agents() {
  const { address } = useWallet();
  const [keys, setKeys] = useState([]);
  const [usage, setUsage] = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [reveal, setReveal] = useState({});
  const [copied, setCopied] = useState(null);

  const load = () => getKeys(address).then(setKeys).catch(() => {});
  useEffect(() => {
    load();
    getUsage(address).then(setUsage).catch(() => {});
    getEndpoints().then(setEndpoints).catch(() => {});
  }, [address]);

  const handleCreate = async () => {
    if (!name.trim()) { toast.error("Name your key"); return; }
    setCreating(true);
    try {
      const k = await createKey({ wallet: address, name: name.trim() });
      setName("");
      setReveal((r) => ({ ...r, [k.id]: true }));
      await load();
      toast.success("API key created", { description: "Copy it now, keep it secret" });
    } catch {
      toast.error("Failed to create key");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    try {
      await revokeKey(id);
      await load();
      toast.success("Key revoked");
    } catch {
      toast.error("Failed to revoke");
    }
  };

  const copy = (id, val) => {
    navigator.clipboard.writeText(val);
    setCopied(id);
    toast.success("Copied");
    setTimeout(() => setCopied(null), 1500);
  };

  const mask = (k) => k.slice(0, 10) + "..." + k.slice(-4);

  return (
    <div className="max-w-5xl">
      <PageHeader eyebrow="developer access" title="Agents API" desc="Programmatic locking and vesting for agents and bots. Issue keys, read usage, ship." />

      <div className="mb-8 grid gap-4 sm:grid-cols-3">
        <StatCard testId="agent-requests" label="Requests (14d)" value={usage ? fmtNum(usage.total_requests) : "--"} accent />
        <StatCard testId="agent-success" label="Success Rate" value={usage ? `${usage.success_rate}%` : "--"} />
        <StatCard testId="agent-latency" label="Avg Latency" value={usage ? `${usage.avg_latency_ms}ms` : "--"} />
      </div>

      {/* Usage chart */}
      <div className="mb-8 border border-welock-border bg-welock-surface p-6" data-testid="usage-chart">
        <span className="eyebrow">api usage</span>
        <h3 className="mb-6 mt-2 font-heading text-lg text-welock-textPrimary">Requests per day</h3>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={usage?.series || []} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#1E222D" vertical={false} />
              <XAxis dataKey="day" stroke="#6B7280" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#1E222D" }} />
              <YAxis stroke="#6B7280" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} width={40} />
              <Tooltip content={<UsageTooltip />} cursor={{ fill: "#171A21" }} />
              <Bar dataKey="requests" fill="#00DC82" radius={[1, 1, 0, 0]} maxBarSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Keys */}
        <div>
          <h3 className="mb-4 flex items-center gap-2 font-heading text-lg text-welock-textPrimary">
            <KeyRound size={16} className="text-welock-accent" /> API keys
          </h3>
          <div className="mb-4 flex gap-2">
            <Input
              data-testid="key-name-input"
              placeholder="Key name (e.g. prod-agent)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              className="border-welock-border bg-welock-bg font-mono text-sm text-welock-textPrimary focus-visible:ring-welock-accent"
            />
            <button
              data-testid="create-key-btn"
              onClick={handleCreate}
              disabled={creating}
              className="flex items-center gap-1.5 whitespace-nowrap bg-welock-accent px-4 py-2 font-mono text-sm font-medium text-welock-bg transition-all hover:bg-welock-accentHover disabled:opacity-60"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Generate
            </button>
          </div>
          <div className="space-y-2" data-testid="keys-list">
            {keys.map((k) => (
              <div key={k.id} data-testid={`key-${k.id}`} className="border border-welock-border bg-welock-surface p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-welock-textPrimary">{k.name}</span>
                  <div className="flex items-center gap-2">
                    <button data-testid={`reveal-${k.id}`} onClick={() => setReveal((r) => ({ ...r, [k.id]: !r[k.id] }))} className="text-welock-textMuted hover:text-welock-textPrimary">
                      {reveal[k.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button data-testid={`copy-${k.id}`} onClick={() => copy(k.id, k.key)} className="text-welock-textMuted hover:text-welock-accent">
                      {copied === k.id ? <Check size={14} className="text-welock-accent" /> : <Copy size={14} />}
                    </button>
                    <button data-testid={`revoke-${k.id}`} onClick={() => handleRevoke(k.id)} className="text-welock-textMuted hover:text-welock-danger">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="mt-2 break-all font-mono text-xs text-welock-textSecondary">
                  {reveal[k.id] ? k.key : mask(k.key)}
                </div>
                <div className="mt-2 flex items-center gap-4 font-mono text-[10px] text-welock-textMuted">
                  <span>{fmtNum(k.requests)} requests</span>
                  <span>created {new Date(k.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
            {keys.length === 0 && (
              <div className="border border-dashed border-welock-border py-10 text-center font-mono text-xs text-welock-textMuted">
                No keys yet. Generate one to start.
              </div>
            )}
          </div>
        </div>

        {/* Endpoints */}
        <div>
          <h3 className="mb-4 flex items-center gap-2 font-heading text-lg text-welock-textPrimary">
            <Terminal size={16} className="text-welock-accent" /> Endpoints
          </h3>
          <div className="border border-welock-border bg-welock-surface" data-testid="endpoints-list">
            {endpoints.map((e, i) => (
              <div key={i} className="border-b border-welock-border/60 p-4 last:border-0">
                <div className="flex items-center gap-3">
                  <span className={`w-11 text-center font-mono text-[10px] font-medium ${e.method === "GET" ? "text-welock-accent" : "text-welock-warning"}`}>
                    {e.method}
                  </span>
                  <span className="font-mono text-xs text-welock-textPrimary">{e.path}</span>
                </div>
                <p className="mt-1.5 pl-14 font-mono text-[11px] text-welock-textMuted">{e.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 border border-welock-border bg-welock-bg p-4">
            <div className="font-mono text-[10px] uppercase tracking-wider text-welock-textMuted">Base URL</div>
            <div className="mt-1.5 font-mono text-xs text-welock-accent">https://api.welock.xyz</div>
          </div>
        </div>
      </div>
    </div>
  );
}
